/**
 * Bridges the CSS design tokens into Recharts, which needs concrete colour
 * strings rather than `var(--…)` in several props.
 *
 * Reading the computed value keeps a single source of truth (tokens.css) and
 * means a theme switch repaints the charts with the theme's own validated
 * steps rather than an automatic flip of the other theme's colours.
 */
export interface ChartTheme {
  series: string[];
  grid: string;
  axis: string;
  surface: string;
  tooltipBg: string;
  tooltipBorder: string;
  text: string;
  textMuted: string;
  signal: string;
}

function token(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value === '' ? fallback : value;
}

export function readChartTheme(): ChartTheme {
  return {
    // Assigned in order, never cycled — see the note in tokens.css.
    series: [
      token('--viz-1', '#9085e9'),
      token('--viz-2', '#199e70'),
      token('--viz-3', '#c98500'),
      token('--viz-4', '#d55181'),
      token('--viz-5', '#3987e5'),
    ],
    grid: token('--viz-grid', 'rgba(255,255,255,0.07)'),
    axis: token('--viz-axis', '#6b7a94'),
    surface: token('--bg-surface', '#111624'),
    tooltipBg: token('--bg-surface-raised', '#161d2e'),
    tooltipBorder: token('--border-default', 'rgba(255,255,255,0.11)'),
    text: token('--text-primary', '#e8ecf5'),
    textMuted: token('--text-tertiary', '#6b7a94'),
    signal: token('--signal', '#22d3ee'),
  };
}
